import { useState, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Fab,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { updateWorkflow, listAgents, listTools, getWorkflow, configureWorkflowSchedule, deleteWorkflowScheduleConfig, configureWorkflowWebhook } 
  from "../../api/agentApi";
import { useNavigate, useParams } from "react-router-dom";
import {
  ReactFlow,
  addEdge,
  Background,
  BackgroundVariant,
  Controls,
  useNodesState,
  useEdgesState,
  Panel,
  Node,
  Edge,
  OnConnect,
  Connection,
  MarkerType,
} from "reactflow";
import AddIcon from "@mui/icons-material/Add";
import "reactflow/dist/style.css";
import WorkflowNode, { WorkflowNodeData } from "./WorkflowNode";
import WorkflowPropertiesDrawer from "./WorkflowPropertiesDrawer";
import { AddNodePopover } from "./AddNodePopover";
import { ConnectionDialog } from "./ConnectionDialog";
import {
  getLayoutedElements,
  handleNodeTypeSelection,
  loadWorkflowNodesWithTriggers,
  createWorkflowNode,
  sanitizeWorkflowName,
  convertNodesToSteps,
  STRUCTURAL_NODE_DEFAULT_LABELS
} from "./workflowUtils";
import { getWebhookLabel } from "./WebhookConfigBuilder";
import { StepType } from './WorkflowTypes';


const nodeTypes = { muiNode: WorkflowNode };

const WorkflowEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { workflowId } = useParams<{ workflowId: string }>();
  
  const [nodes, setNodes, onNodesChange] = useNodesState<WorkflowNodeData>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // UI State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [popoverStep, setPopoverStep] = useState<"select" | "config">("select");
  const [selectedType, setSelectedType] = useState<StepType | "trigger" | null>(null);

  // Form State
  const [formData, setFormData] = useState<{
    id: string;
    version: string;
    prompt: string;
    triggerType: "schedule" | "webhook";
    show_status?: boolean;
    cron: string;
    webhookConfig: {
      adapter_type: string;
      secrets: Record<string, string>;
    };
  }>({
    id: "",
    version: "1",
    prompt: "",
    triggerType: "schedule",
    cron: "",
    show_status: false,
    webhookConfig: {
      adapter_type: "slack",
      secrets: {},
    },
  });
  const [agents, setAgents] = useState<any[]>([]);
  const [tools, setTools] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Connection Dialog State
  const [connectionDialogOpen, setConnectionDialogOpen] = useState(false);
  const [pendingConnection, setPendingConnection] = useState<Connection | null>(null);
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([]);

  // Workflow Metadata State
  const [workflowName, setWorkflowName] = useState("");
  const [workflowDisplayName, setWorkflowDisplayName] = useState("");
  const [workflowDescription, setWorkflowDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  
  // Track original trigger nodes to detect deletions
  const [originalTriggerNodes, setOriginalTriggerNodes] = useState<Node<WorkflowNodeData>[]>([]);

  // Preserve loaded global_timeout_seconds from the existing workflow
  const [loadedGlobalTimeout, setLoadedGlobalTimeout] = useState<number | null>(null);

  // Workflow version (loaded from API, used for localStorage key)
  const [workflowVersion, setWorkflowVersion] = useState<number>(1);

  // Property Drawer State
  const [selectedElement, setSelectedElement] = useState<Node<WorkflowNodeData> | Edge | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // localStorage key: wf-{id}-{version}
  const editLayoutKey = workflowId ? `wf-${workflowId}-${workflowVersion}` : null;

  // Toggle for showing input_mapping data flow edges (persisted in localStorage)
  const [showDataMappings, setShowDataMappings] = useState(false);

  // Initialize toggle from localStorage once we have the key (after workflow loads)
  useEffect(() => {
    if (!editLayoutKey) return;
    try {
      const saved = localStorage.getItem(editLayoutKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed._showDataMappings === true) {
          setShowDataMappings(true);
        }
      }
    } catch { /* ignore */ }
  }, [editLayoutKey]);

  // Filter edges: data flow edges (from loadWorkflowNodesWithTriggers) are tagged with data.isDataFlow
  const displayedEdges = useMemo(
    () => showDataMappings ? edges : edges.filter(e => !(e.data as any)?.isDataFlow),
    [edges, showDataMappings]
  );

  // Persist toggle to localStorage
  const handleToggleDataMappings = useCallback((checked: boolean) => {
    setShowDataMappings(checked);
    if (!editLayoutKey) return;
    try {
      const existing = localStorage.getItem(editLayoutKey);
      const parsed = existing ? JSON.parse(existing) : {};
      parsed._showDataMappings = checked;
      localStorage.setItem(editLayoutKey, JSON.stringify(parsed));
    } catch { /* ignore */ }
  }, [editLayoutKey]);

  // Save node positions to localStorage on drag stop
  const handleNodeDragStop = useCallback((_event: React.MouseEvent, _node: Node) => {
    if (!editLayoutKey) return;
    setNodes((currentNodes) => {
      try {
        const existing = localStorage.getItem(editLayoutKey);
        const parsed = existing ? JSON.parse(existing) : {};
        currentNodes.forEach((n) => { parsed[n.id] = n.position; });
        localStorage.setItem(editLayoutKey, JSON.stringify(parsed));
      } catch { /* ignore */ }
      return currentNodes;
    });
  }, [editLayoutKey, setNodes]);

  const onNodeClick = useCallback((event: React.MouseEvent, node: Node<WorkflowNodeData>) => {
    setSelectedElement(node);
    setDrawerOpen(true);
  }, []);

  const onEdgeClick = useCallback((event: React.MouseEvent, edge: Edge) => {
    setSelectedElement(edge);
    setDrawerOpen(true);
  }, []);
  
  const onPaneClick = useCallback(() => {
    // Optional: Close drawer when clicking background?
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [a, t] = await Promise.all([listAgents(), listTools()]);
        const agentList = Array.isArray(a) ? a : a?.agents || [];
        // Sort agents by date descending (newest first)
        const sortedAgents = agentList.sort((x: any, y: any) => {
          const dateX = new Date(x.date || 0).getTime();
          const dateY = new Date(y.date || 0).getTime();
          return dateY - dateX;
        });
        setAgents(sortedAgents);
        setTools(Array.isArray(t) ? t : t?.tools || []);
      } catch (err) {
        setError("Failed to fetch data");
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    const loadExistingWorkflow = async () => {
      if (!workflowId) return;

      try {
        const workflow = await getWorkflow(workflowId);
        if (workflow) {
          setWorkflowName(workflow.name);
          setWorkflowVersion(workflow.version ?? 1);
          if (workflow.dag_definition) {
            if (workflow.dag_definition.description) {
              setWorkflowDescription(workflow.dag_definition.description);
            }
            // Restore display_name; fall back to name if not present (older workflows)
            setWorkflowDisplayName(workflow.dag_definition.display_name || workflow.name);
            if (workflow.dag_definition.global_timeout_seconds) {
              setLoadedGlobalTimeout(workflow.dag_definition.global_timeout_seconds);
            }
          }

          // Load workflow nodes and edges with triggers (from workflow response)
          // schedule_config and webhook_config are part of the workflow response
          const { nodes: initialNodes, edges: initialEdges } = await loadWorkflowNodesWithTriggers(
            workflow,
            'default',
            { type: MarkerType.ArrowClosed }
          );

          // Apply layout
          const layouted = getLayoutedElements(initialNodes, initialEdges);

          // Restore saved positions from localStorage if available
          const savedKey = `wf-${workflowId}-${workflow.version ?? 1}`;
          const savedLayout = localStorage.getItem(savedKey);
          let finalNodes = layouted.nodes;
          if (savedLayout) {
            try {
              const parsed = JSON.parse(savedLayout);
              finalNodes = layouted.nodes.map((node) => {
                const saved = parsed[node.id];
                if (saved && typeof saved === 'object' && 'x' in saved && 'y' in saved) {
                  return { ...node, position: saved };
                }
                return node;
              });
            } catch { /* ignore corrupt data */ }
          }

          setNodes(finalNodes);
          setEdges(layouted.edges);
          
          // Store original trigger nodes to detect deletions later
          const triggerNodes = layouted.nodes.filter(node => node.data.type === "trigger");
          setOriginalTriggerNodes(triggerNodes);
        }
      } catch (e) {
        console.error("Failed to load workflow", e);
        setError("Failed to load existing workflow");
      }
    };
    loadExistingWorkflow();
  }, [workflowId, setNodes, setEdges]);

  const defaultEdgeOptions = {
    type: "default", // Or 'default' for standard Bezier curves
    animated: false,
    // style: { strokeWidth: 2, stroke: "#94a3b8" },
  };


  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => {
    setAnchorEl(null);
    setPopoverStep("select");
    setSelectedType(null);
    setFormData({
      id: "",
      version: "1",
      prompt: "",
      triggerType: "schedule",
      cron: "",
      webhookConfig: {
        adapter_type: "slack",
        secrets: {},
      },
    });
  };

  const handleTypeSelect = (optionId: string) => {
    const { selectedType: newType, formData: newFormData } = handleNodeTypeSelection(optionId, formData);
    setSelectedType(newType);
    setFormData(newFormData);
    setPopoverStep("config");
  };

  const startConfig = (type: "agent" | "tool") => {
    setSelectedType(type);
    setPopoverStep("config");
  };

  const onConnect: OnConnect = useCallback(
    (params: Connection) => {
      // Get source and target nodes
      const sourceNode = nodes.find((n) => n.id === params.source);
      const targetNode = nodes.find((n) => n.id === params.target);

      // 1. Validation: Prevent connecting TO a trigger
      if (targetNode?.data.type === "trigger") {
        return;
      }

      // 2. Specialized logic for Trigger Sources
      if (sourceNode?.data.type === "trigger") {
        // DAG Constraint: Trigger can only have ONE outgoing edge
        const existingTriggerEdge = edges.find(e => e.source === params.source);
        if (existingTriggerEdge) {
          alert("Trigger can only connect to one step (DAG constraint). Please remove the existing connection first.");
          return;
        }
        
        // Just add the visual edge to show the flow
        setEdges((eds) => addEdge({ ...params, type: "default" }, eds));

        // We explicitly DO NOT update setNodes here.
        // This ensures depends_on and input_mapping remain empty for this connection.
        return;
      }

      // 3. Standard logic for Agent/Tool connections (Show Dialog)
      setPendingConnection(params);
      // Default to picking the source of the connection
      if (params.source) {
        setSelectedSourceIds([params.source]);
      }
      setConnectionDialogOpen(true);
    },
    [nodes, setEdges]
  );

  const handleSaveWorkflow = async () => {
    setIsSaving(true);
    try {
      // Convert nodes to DAG steps using the shared utility (pass edges for depends_on derivation)
      const steps = convertNodesToSteps(nodes, edges);

      if (steps.length === 0) {
        alert("Workflow must have at least one Agent or Tool step.");
        setIsSaving(false);
        return;
      }

      // Reject agent steps with no agent assigned
      const missingAgentSteps = steps.filter(
        (s: any) => s.type === 'agent' && (!s.agent_id || s.agent_id.toUpperCase() === 'PLACEHOLDER')
      );
      if (missingAgentSteps.length > 0) {
        const names = missingAgentSteps.map((s: any) => s.id || s.name).join(', ');
        alert(`Cannot save: ${missingAgentSteps.length} agent step(s) have no agent assigned (${names}). Assign an agent to each step first.`);
        setIsSaving(false);
        return;
      }

      // Calculate global timeout from step timeouts (cap at 24h backend limit)
      let totalTimeout = 0;
      nodes.forEach(node => {
        if (node.data.type === 'trigger') return;
        totalTimeout += node.data.rawData?.timeout_seconds || node.data.config?.timeout_seconds || 300;
        if (node.data.type === 'human_input') {
          totalTimeout += (node.data.config?.timeout_minutes || node.data.rawData?.timeout_minutes || 0) * 60;
        }
        totalTimeout += 300;
      });
      const globalTimeout = loadedGlobalTimeout || Math.max(totalTimeout, 1800);

      const dagDefinition = {
        workflow_name: sanitizeWorkflowName(workflowName),
        display_name: workflowDisplayName || workflowName,
        description: workflowDescription,
        steps,
        global_timeout_seconds: globalTimeout,
      };

      console.log("Constructed DAG:", dagDefinition);

      // 5. Call API - Update existing workflow (creates new version)
      if (!workflowId) {
        throw new Error("Workflow ID is required for update");
      }
      
      const response = await updateWorkflow(workflowId, workflowName, dagDefinition);
      console.log("Workflow updated, new version:", response.version);
      
      // 6. Handle schedule trigger changes
      const currentTriggerNodes = nodes.filter(node => node.data.type === "trigger");
      const currentScheduleTriggers = currentTriggerNodes.filter(node => node.data.config.triggerType === "schedule");
      
      // Check if original workflow had a schedule trigger that was deleted
      const originalScheduleTriggers = originalTriggerNodes.filter(node => node.data.type === "trigger" && node.data.config.triggerType === "schedule");
      const hadScheduleTrigger = originalScheduleTriggers.length > 0;
      const hasScheduleTrigger = currentScheduleTriggers.length > 0;
      
      if (hadScheduleTrigger && !hasScheduleTrigger) {
        // Schedule trigger was deleted - remove the schedule config
        try {
          console.log("Deleting schedule configuration (trigger node removed)");
          await deleteWorkflowScheduleConfig(workflowId);
          console.log("✅ Schedule configuration deleted");
        } catch (deleteErr) {
          const errorMsg = deleteErr instanceof Error ? deleteErr.message : String(deleteErr);
          console.error("Failed to delete schedule config:", errorMsg);
          alert(`Warning: Workflow updated but failed to delete schedule configuration: ${errorMsg}`);
        }
      } else if (hasScheduleTrigger) {
        // Configure/update schedule triggers
        for (const triggerNode of currentScheduleTriggers) {
          const cronExpression = triggerNode.data.config.cron;
          if (cronExpression) {
            try {
              console.log("Configuring schedule trigger:", { workflowId, cronExpression });
              await configureWorkflowSchedule(workflowId, {
                cron_expression: cronExpression,
                enabled: true
              });
              console.log("✅ Schedule trigger configured");
            } catch (scheduleErr) {
              const errorMsg = scheduleErr instanceof Error ? scheduleErr.message : String(scheduleErr);
              console.error("Schedule config error:", errorMsg);
              alert(`Warning: Workflow updated but schedule configuration failed: ${errorMsg}`);
            }
          }
        }
      }
      
      // 7. Handle webhook trigger changes
      const currentWebhookTriggers = currentTriggerNodes.filter(node => node.data.config.triggerType === "webhook");
      const originalWebhookTriggers = originalTriggerNodes.filter(node => node.data.type === "trigger" && node.data.config.triggerType === "webhook");
      const hadWebhookTrigger = originalWebhookTriggers.length > 0;
      const hasWebhookTrigger = currentWebhookTriggers.length > 0;
      
      if (hasWebhookTrigger) {
        // Configure/update webhook triggers — but only if secrets were explicitly set
        // (loaded nodes have empty secrets since the GET response redacts them;
        // the backend already copies triggers on version bump, so skip the PUT
        // to avoid overwriting with empty secrets_ref)
        for (const triggerNode of currentWebhookTriggers) {
          const webhookConfig = triggerNode.data.config.webhookConfig;
          if (webhookConfig && webhookConfig.adapter_type && webhookConfig.secrets
              && Object.keys(webhookConfig.secrets).length > 0) {
            try {
              console.log("Configuring webhook trigger:", {
                workflowId,
                adapter_type: webhookConfig.adapter_type
              });

              await configureWorkflowWebhook(workflowId, {
                enabled: true,
                adapter_type: webhookConfig.adapter_type,
                secrets_ref: webhookConfig.secrets
              });

              console.log("✅ Webhook trigger configured");
            } catch (webhookErr) {
              const errorMsg = webhookErr instanceof Error ? webhookErr.message : String(webhookErr);
              console.error("Webhook config error:", errorMsg);
              alert(`Warning: Workflow updated but webhook configuration failed: ${errorMsg}`);
            }
          } else {
            console.log("Skipping webhook config PUT (secrets unchanged, backend copies trigger on version bump)");
          }
        }
      }
      
      setSaveDialogOpen(false);
      alert(`Workflow updated successfully! New version: ${response.version}`);
      navigate(`/workflow/${workflowId}`); // Redirect back to detail
    } catch (err: unknown) {
      console.error("Failed to save workflow:", err);
      const errorMsg = err instanceof Error ? err.message : String(err);
      alert(`Error saving workflow: ${errorMsg}`);
      setError("Failed to save workflow");
    } finally {
      setIsSaving(false);
    }
  };

  const handleConnectionSave = () => {
    if (!pendingConnection) return;

    // 1. Create the edges
    const newEdges: Edge[] = [];
    selectedSourceIds.forEach(sourceId => {
      newEdges.push({
        id: `e-${sourceId}-${pendingConnection.target}`,
        source: sourceId,
        target: pendingConnection.target!,
        type: 'default',
        // markerEnd: { type: MarkerType.ArrowClosed },
      });
    });

    setEdges((eds) => {
      const existingIds = new Set(eds.map(e => e.id));
      const textIds = newEdges.filter(e => !existingIds.has(e.id));
      return [...eds, ...textIds];
    });

    // Correct way to update mappings with proper node lookup
    setNodes((currentNodes) => {
      return currentNodes.map((targetNode) => {
        if (targetNode.id === pendingConnection.target) {
          const config = targetNode.data.config;
          const newDependsOn = new Set(config.depends_on || []);
          const newInputMapping = { ...(config.input_mapping || {}) };

          selectedSourceIds.forEach((sourceId) => {
            newDependsOn.add(sourceId);
            
            // Find Source Node for Label
            const sourceNode = currentNodes.find(n => n.id === sourceId);
            if (sourceNode) {
              const label = sourceNode.data.label;
              // Derive slug the same way generateSlug does (without uniqueness, that's fine for display)
              let slugBase = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
              if (!slugBase) slugBase = 'step';
              // Truncate to match STEP_ID_MAX_LENGTH (512) minus room for suffix
              if (slugBase.length > 509) slugBase = slugBase.substring(0, 509).replace(/_+$/, '');
              const key = slugBase + "_output";
              newInputMapping[key] = `$.steps.${sourceNode.id}.output`;
            }
          });

          const updatedNode = {
            ...targetNode,
            data: {
              ...targetNode.data,
              config: {
                ...config,
                depends_on: Array.from(newDependsOn),
                input_mapping: newInputMapping,
              }
            }
          };
          console.log("Updated Node (setNodes):", updatedNode);
          return updatedNode;
        }
        return targetNode;
      });
    });

    setConnectionDialogOpen(false);
    setPendingConnection(null);
  };

  const finalizeAddNode = () => {
    let label = "";

    if (selectedType === "trigger") {
      // Check if trigger of this type already exists
      const existingTrigger = nodes.find(
        (n) => n.data.type === "trigger" && n.data.config.triggerType === formData.triggerType
      );
      
      if (existingTrigger) {
        alert(`A ${formData.triggerType} trigger already exists. Only one ${formData.triggerType} trigger is allowed per workflow.`);
        return;
      }
      
      // Triggers are manual, they don't use the agents/tools lists
      if (formData.triggerType === "schedule") {
        label = "Schedule Trigger";
      } else {
        // Webhook trigger - use descriptive label based on adapter type
        label = getWebhookLabel(formData.webhookConfig?.adapter_type || "generic");
      }
    } else if (STRUCTURAL_NODE_DEFAULT_LABELS[selectedType!]) {
      // Structural node types get a meaningful default label
      label = STRUCTURAL_NODE_DEFAULT_LABELS[selectedType!];
    } else {
      // Standard Agent/Tool lookup
      const selectedItem = (selectedType === "agent" ? agents : tools).find((i) =>
        selectedType === "agent"
          ? i.agent_id === formData.id
          : i.id === formData.id
      );
      label = selectedItem?.display_name || formData.id || "New Step";
    }

    // Use utility function to create new node
    const newNode = createWorkflowNode(selectedType!, formData, label, nodes.length);
    
    setNodes((nds) => [...nds, newNode]);
    handleClose();
  };

  return (
    <Box sx={{ pt: 1, px: 3 }}>
      <Toolbar disableGutters sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
        <Typography variant="h4" sx={{ pl: 0, mr: 2 }}>
          Edit Workflow
        </Typography>
        
        <Box sx={{ flexGrow: 1 }} />
        <Button 
          variant="outlined" 
          onClick={() => navigate(`/workflow/${workflowId}`)}
          sx={{ mr: 2 }}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => setSaveDialogOpen(true)}
          disabled={isSaving || nodes.length === 0}
        >
          Update Workflow
        </Button>
      </Toolbar>
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            width: "100%",
            height: "600px",
            border: "1px solid #e0e0e0",
            position: "relative",
          }}
        >
          <ReactFlow
            nodes={nodes}
            edges={displayedEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onNodeDragStop={handleNodeDragStop}
            defaultEdgeOptions={defaultEdgeOptions}
            nodeTypes={nodeTypes}
            fitView
          >
            <Background variant={BackgroundVariant.Dots} />
            <Controls />

            <Panel position="top-left">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Fab color="primary" size="small" onClick={handleOpen}>
                  <AddIcon />
                </Fab>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showDataMappings}
                      onChange={(e) => handleToggleDataMappings(e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '11px', color: '#64748B', userSelect: 'none' }}>
                      Show data mappings
                    </Typography>
                  }
                  sx={{
                    ml: 0.5,
                    bgcolor: 'rgba(255,255,255,0.9)',
                    borderRadius: '8px',
                    px: 1,
                    py: 0.25,
                    border: '1px solid #e2e8f0',
                  }}
                />
              </Box>

              <AddNodePopover
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                selectedType={selectedType}
                onTypeSelect={handleTypeSelect}
                onBackClick={() => {
                  setSelectedType(null);
                  setPopoverStep("select");
                }}
                formData={formData}
                onFormDataChange={(updates) =>
                  setFormData((prev) => ({ ...prev, ...updates }))
                }
                agents={agents}
                tools={tools}
                onAddNode={finalizeAddNode}
                nodes={nodes}
                edges={edges}
                setNodes={setNodes}
                setEdges={setEdges}
              />
            </Panel>
          </ReactFlow>
        </Box>

        {/* CONNECTION CONFIG DIALOG */}
        <ConnectionDialog
          open={connectionDialogOpen}
          onClose={() => setConnectionDialogOpen(false)}
          nodes={nodes}
          targetNodeId={pendingConnection?.target || ''}
          selectedSourceIds={selectedSourceIds}
          onSourceIdsChange={setSelectedSourceIds}
          onSave={handleConnectionSave}
        />

        {/* SAVE WORKFLOW DIALOG */}
        <Dialog 
          open={saveDialogOpen} 
          onClose={() => setSaveDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Update Workflow</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
              This will create a new version of the workflow while preserving the previous versions. The new version will be marked as current.
            </Typography>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                label="Display Name"
                fullWidth
                value={workflowDisplayName}
                onChange={(e) => setWorkflowDisplayName(e.target.value)}
                placeholder="e.g. Marketing Campaign Generator"
                helperText={`ID: ${workflowName} (cannot be changed)`}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={workflowDescription}
                onChange={(e) => setWorkflowDescription(e.target.value)}
                placeholder="Describe what this workflow does..."
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveDialogOpen(false)} disabled={isSaving}>Cancel</Button>
            <Button 
              onClick={handleSaveWorkflow} 
              variant="contained" 
              disabled={isSaving}
            >
              {isSaving ? "Updating..." : "Update"}
            </Button>
          </DialogActions>
        </Dialog>

        <WorkflowPropertiesDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            selectedElement={selectedElement}
            nodes={nodes}
            workflowId={workflowId}
            onUpdateNode={(nodeId, updates) => {
              setNodes((nds) =>
                nds.map((node) =>
                  node.id === nodeId
                    ? { ...node, data: { ...node.data, ...updates } as WorkflowNodeData }
                    : node
                )
              );
              // Keep selectedElement in sync so the drawer re-initializes with the saved state
              setSelectedElement((prev) => {
                if (prev && 'data' in prev && prev.id === nodeId) {
                  return { ...prev, data: { ...prev.data, ...updates } as WorkflowNodeData };
                }
                return prev;
              });
            }}
        />
      </Paper>
    </Box>
  );
};

export default WorkflowEditPage;
